/**
 * JCA Pay — checkout button (Web Component)
 * Built by JCA Creative for reuse across JCA Creative client stores.
 *
 * Talks to the JCA Pay API (see the separate jca-pay-api repository) instead
 * of computing or sending any price itself. The browser only ever states
 * which variant and how many units; the API returns the real price, and this
 * component just displays it.
 *
 * Plain vanilla custom element (no @theme/component import) to match every
 * other zc-*.js script in this theme — none of them use that base
 * class, so this doesn't introduce a new dependency style.
 */
(function () {
  /**
   * Postal code ranges: all of Spain except the Canary Islands (35/38) and
   * Ceuta/Melilla (51/52) — those are excluded from delivery entirely (see
   * the province <select>, which only lists mainland Spain + Baleares).
   * NOTE: 36 (Pontevedra) and 37 (Salamanca) are mainland provinces and MUST
   * stay allowed — only 35 and 38 are the Canary Islands' province prefixes.
   */
  var ZIP_PATTERN = /^(?:0[1-9]|[12]\d|30|31|32|33|34|36|37|39|4\d|50)\d{3}$/;

  /**
   * Phone validation depends on the selected country prefix (a <select> next
   * to the number, defaulting to +34) — a 9-digit Spanish rule doesn't fit a
   * 10-digit US number, so each mapped prefix gets its own pattern + message.
   * Anything not listed falls back to a generic "plausible phone number"
   * digit-count check rather than rejecting it outright.
   */
  var PHONE_RULES_BY_PREFIX = {
    '+34': { pattern: /^[6789]\d{8}$/, message: 'Introduce un móvil español válido de 9 dígitos (empieza por 6, 7, 8 o 9).' },
    '+351': { pattern: /^9\d{8}$/, message: 'Introduce un móvil portugués válido de 9 dígitos (empieza por 9).' },
    '+33': { pattern: /^[67]\d{8}$/, message: 'Introduce un móvil francés válido de 9 dígitos (empieza por 6 o 7).' },
    '+44': { pattern: /^7\d{9}$/, message: 'Introduce un móvil del Reino Unido válido de 10 dígitos (empieza por 7).' },
    '+49': { pattern: /^1\d{9,10}$/, message: 'Introduce un móvil alemán válido (empieza por 1, 10-11 dígitos).' },
    '+39': { pattern: /^3\d{8,9}$/, message: 'Introduce un móvil italiano válido (empieza por 3, 9-10 dígitos).' },
    '+1': { pattern: /^\d{10}$/, message: 'Introduce un número de 10 dígitos.' },
    '+52': { pattern: /^\d{10}$/, message: 'Introduce un móvil mexicano válido de 10 dígitos.' },
    '+54': { pattern: /^9?\d{10}$/, message: 'Introduce un móvil argentino válido de 10-11 dígitos.' },
    '+55': { pattern: /^\d{10,11}$/, message: 'Introduce un móvil brasileño válido de 10-11 dígitos.' },
    '+56': { pattern: /^9\d{8}$/, message: 'Introduce un móvil chileno válido de 9 dígitos (empieza por 9).' },
    '+57': { pattern: /^3\d{9}$/, message: 'Introduce un móvil colombiano válido de 10 dígitos (empieza por 3).' },
  };
  /** Fallback for any prefix not mapped above: a plausible-length, digits-only number. */
  /**
   * What shoppers actually type or autofill: "612 34 56 78", "612-345-678",
   * "+34 612 345 678", "0034612345678". The rules above expect bare national
   * digits, so validating the raw value told real customers their (valid)
   * number was wrong. Strip everything that isn't a digit, then drop a
   * leading country code (with or without 00) when what remains is a valid
   * national number for the selected prefix.
   */
  function normalizePhone(value, prefix, rule) {
    var digits = String(value || '').replace(/\D/g, '');
    var cc = String(prefix || '').replace(/\D/g, '');
    if (!cc || rule.pattern.test(digits)) return digits;
    var candidates = ['00' + cc, cc];
    for (var i = 0; i < candidates.length; i++) {
      if (digits.indexOf(candidates[i]) === 0 && rule.pattern.test(digits.slice(candidates[i].length))) {
        return digits.slice(candidates[i].length);
      }
    }
    return digits;
  }

  var DEFAULT_PHONE_RULE = { pattern: /^\d{6,12}$/, message: 'Introduce un número de teléfono válido (solo dígitos, 6 a 12 cifras).' };

  /**
   * Spanish postal code → province, by INE's 2-digit prefix. Auto-selecting
   * the matching province the moment a valid zip is typed removes a manual
   * step — entered independently, zip and province commonly disagree (a
   * typo in either), and that mismatch previously wasn't caught until the
   * API resolved shipping, by which point the shopper had already moved on.
   */
  var PROVINCE_BY_ZIP_PREFIX = {
    '01': 'Álava', '02': 'Albacete', '03': 'Alicante', '04': 'Almería', '05': 'Ávila',
    '06': 'Badajoz', '07': 'Islas Baleares', '08': 'Barcelona', '09': 'Burgos',
    '10': 'Cáceres', '11': 'Cádiz', '12': 'Castellón', '13': 'Ciudad Real', '14': 'Córdoba',
    '15': 'A Coruña', '16': 'Cuenca', '17': 'Girona', '18': 'Granada', '19': 'Guadalajara',
    '20': 'Guipúzcoa', '21': 'Huelva', '22': 'Huesca', '23': 'Jaén', '24': 'León',
    '25': 'Lleida', '26': 'La Rioja', '27': 'Lugo', '28': 'Madrid', '29': 'Málaga',
    '30': 'Murcia', '31': 'Navarra', '32': 'Ourense', '33': 'Asturias', '34': 'Palencia',
    '36': 'Pontevedra', '37': 'Salamanca', '39': 'Cantabria', '40': 'Segovia', '41': 'Sevilla',
    '42': 'Soria', '43': 'Tarragona', '44': 'Teruel', '45': 'Toledo', '46': 'Valencia',
    '47': 'Valladolid', '48': 'Vizcaya', '49': 'Zamora', '50': 'Zaragoza',
  };

  /**
   * Per-field validation rules with human-readable messages for each failure
   * mode, so the shopper sees exactly what's wrong instead of a bare red
   * border. Checked in order: required → pattern → length.
   * `phone` is intentionally absent — see phoneRule(), it depends on the
   * selected country prefix and is validated separately.
   *
   * This mirrors (but does not replace) the API's own server-side validation
   * — the API never trusts the browser either. Keeping both in sync matters
   * for UX (instant feedback) but not for security: a bypassed or disabled
   * check here still gets rejected server-side.
   */
  var FIELD_RULES = {
    fullName: {
      required: 'Escribe tu nombre.',
      // The API rejects anything shorter — caught here, next to the field.
      minLength: 2,
      tooShort: 'Escribe tu nombre completo.',
      maxLength: 60,
      tooLong: 'Ese nombre es demasiado largo (máximo 60 caracteres).',
      // A single word is fine — the trailing `*` (not `+`) doesn't force a
      // surname. Some shoppers only want to give a first name, and there's
      // no real reason to block that.
      // ’ is the apostrophe the iPhone keyboard types; · is Catalan l·l;
      // ª/º cover "Mª José"; a dot covers initials.
      pattern: /^[A-Za-zÀ-ÖØ-öø-ÿªº'’·.-]+(?:\s+[A-Za-zÀ-ÖØ-öø-ÿªº'’·.-]+)*$/,
      patternMessage: 'Escribe tu nombre solo con letras (ej: María o María González).',
    },
    address: {
      required: 'Escribe tu dirección.',
      minLength: 5,
      maxLength: 120,
      tooShort: 'Esa dirección es demasiado corta — añade calle y número.',
      tooLong: 'Esa dirección es demasiado larga (máximo 120 caracteres).',
    },
    city: {
      required: 'Escribe tu ciudad.',
      minLength: 2,
      tooShort: 'Escribe el nombre completo de tu ciudad.',
      maxLength: 60,
      tooLong: 'Ese nombre de ciudad es demasiado largo (máximo 60 caracteres).',
      // Official bilingual names ("Alacant/Alicante", "Donostia/San Sebastián"),
      // the iPhone's ’ ("L’Hospitalet"), abbreviations ("Sta. Cruz") and a
      // province in brackets are all real ways people write their town.
      pattern: /^[A-Za-zÀ-ÖØ-öø-ÿ0-9ªº'’·.,()\/\- ]+$/,
      patternMessage: 'La ciudad solo puede tener letras, espacios y guiones.',
    },
    zip: {
      required: 'Escribe tu código postal.',
      maxLength: 5,
      pattern: ZIP_PATTERN,
      patternMessage:
        'Ese código postal no es válido, o pertenece a una zona sin envío (Canarias, Ceuta o Melilla no están disponibles).',
    },
  };

  /**
   * fetch() with a time limit and automatic retries for what a phone on a bad
   * connection actually hits: no network, a request that hangs, a 5xx, a 429
   * (and, for orders, a 409 "still processing"). Any other 4xx is final — the
   * request itself is wrong — and comes straight back. Both calls that use it
   * are safe to repeat: a session is only a price quote, and the API answers
   * the SAME order for the same session instead of creating a second one.
   */
  function fetchWithRetry(url, options, settings) {
    var attempts = settings.attempts;
    var retryStatuses = settings.retryStatuses || [];
    var attempt = 0;
    function wait() {
      return new Promise(function (resolve) { setTimeout(resolve, 1000 * attempt); });
    }
    function once() {
      attempt++;
      if (settings.onAttempt) settings.onAttempt(attempt);
      var controller = typeof AbortController === 'function' ? new AbortController() : null;
      var timer = controller ? setTimeout(function () { controller.abort(); }, settings.timeoutMs) : null;
      var request = controller ? Object.assign({}, options, { signal: controller.signal }) : options;
      return fetch(url, request).then(
        function (response) {
          if (timer) clearTimeout(timer);
          var retryable = response.status >= 500 || response.status === 429 || retryStatuses.indexOf(response.status) !== -1;
          if (retryable && attempt < attempts) return wait().then(once);
          return response;
        },
        function (error) {
          if (timer) clearTimeout(timer);
          if (attempt < attempts) return wait().then(once);
          throw error;
        }
      );
    }
    return once();
  }

  /** Minimum time (ms) the drawer must be open before a submit is accepted — a submit faster than this is a bot filling the form programmatically, not a shopper reading it. */
  var MIN_FILL_TIME_MS = 1500;

  /** All step-1 text fields that get blur/input validation — FIELD_RULES plus phone, which is validated separately (see phoneRule). */
  var VALIDATED_FIELDS = Object.keys(FIELD_RULES).concat(['phone', 'orderEmail']);

  /** Optional email (step 1): only checked when typed; validateField() treats an empty `required` as optional. */
  FIELD_RULES.orderEmail = {
    required: '',
    maxLength: 120,
    tooLong: 'Ese email es demasiado largo.',
    pattern: /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/,
    patternMessage: 'Revisa tu email (por ejemplo nombre@gmail.com) o déjalo vacío.',
  };

  /** Built once and reused: constructing an Intl.NumberFormat has real cost, and renderTotals() calls formatMoney() several times per render. */
  var EUR_FORMATTER = new Intl.NumberFormat('es-ES', { style: 'currency', currency: 'EUR' });

  /**
   * Marketing attribution, captured once per ad click and carried across
   * pages via localStorage — the page an ad sends someone to is rarely the
   * page they eventually check out from, so reading location.search at
   * submit time alone would miss it entirely.
   */
  var ATTRIBUTION_STORAGE_KEY = 'zc_cod_attribution';
  var ATTRIBUTION_TTL_MS = 30 * 24 * 60 * 60 * 1000; // 30 days
  var UTM_KEYS = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content'];

  /**
   * JCA Pay rejects the WHOLE order if an attribution field is over its
   * limit (150 for utm_*, 500 for landing_page/referrer). Meta ad links carry
   * fbclid and long utm values, so a raw location.href easily passes 500:
   * on 2026-10-04 that turned a real Instagram order into "Los datos del
   * pedido no son validos". Attribution is display-only and must never cost
   * a sale, so every value is cut to its limit before it is sent.
   */
  function capAttribution(value, max) {
    return typeof value === 'string' ? value.trim().slice(0, max) : undefined;
  }

  /** Also done from the <head> of every page by snippets/zc-attribution.liquid (this file only loads on product pages) — keep both in step. Runs once at module load: if this landing has UTM params, it's a fresh ad click — snapshot it, overwriting whatever was stored before. No UTM params means keep whatever is already there (a previous click, still within TTL, or nothing). */
  function captureAttribution() {
    try {
      var params = new URLSearchParams(window.location.search);
      if (!UTM_KEYS.some(function (key) { return params.has(key); }) && !params.has('fbclid')) return;

      var attribution = { capturedAt: Date.now(), landingPage: capAttribution(window.location.href, 500), referrer: capAttribution(document.referrer || '', 500) };
      UTM_KEYS.forEach(function (key) {
        var value = params.get(key);
        if (value) attribution[key] = value.slice(0, 150);
      });
      // Kept on its own: landing_page is cut at 500 characters and the click
      // id is what lets Meta tie the purchase back to the ad (see metaContext).
      var fbclid = params.get('fbclid');
      if (fbclid) attribution.fbclid = fbclid.slice(0, 300);
      localStorage.setItem(ATTRIBUTION_STORAGE_KEY, JSON.stringify(attribution));
    } catch (e) {
      // Storage can throw (private browsing, disabled storage) — attribution
      // is a nice-to-have that must never break the page.
    }
  }

  function readAttribution() {
    try {
      var raw = localStorage.getItem(ATTRIBUTION_STORAGE_KEY);
      if (!raw) return null;
      var parsed = JSON.parse(raw);
      if (!parsed || !parsed.capturedAt || Date.now() - parsed.capturedAt > ATTRIBUTION_TTL_MS) return null;
      return parsed;
    } catch (e) {
      return null;
    }
  }

  captureAttribution();

  function readCookie(name) {
    try {
      var match = document.cookie.match(new RegExp('(?:^|; )' + name + '=([^;]*)'));
      return match ? decodeURIComponent(match[1]) : undefined;
    } catch (e) {
      return undefined;
    }
  }

  /**
   * Sent with the order so JCA Pay can report the purchase to Meta itself
   * (Conversions API). The browser pixel only fires for shoppers who accepted
   * cookies and have nothing blocking it — on 2026-10-05 Meta saw 2 of 5
   * orders. event_id is the same one this form's fbq('Purchase') uses, so
   * when both copies arrive Meta keeps one.
   */
  function metaContext(eventId, attribution) {
    var context = { event_id: eventId, page_url: window.location.href.slice(0, 500) };
    var fbp = readCookie('_fbp');
    if (fbp) context.fbp = fbp.slice(0, 200);
    var fbc = readCookie('_fbc');
    if (!fbc && attribution && attribution.fbclid) fbc = 'fb.1.' + attribution.capturedAt + '.' + attribution.fbclid;
    if (fbc) context.fbc = fbc.slice(0, 500);
    // Whether this browser's own pixel is running (cookies accepted, nothing
    // blocking it), i.e. whether Meta also gets a browser copy of the
    // purchase. Diagnostic only: JCA Pay records it and never forwards it.
    context.pixel_loaded = typeof window.fbq === 'function';
    return context;
  }

  function escapeHtml(value) {
    var div = document.createElement('div');
    div.textContent = value == null ? '' : value;
    // Also safe inside a quoted attribute (src="…"): textContent → innerHTML leaves quotes as they are.
    return div.innerHTML.replace(/"/g, '&quot;').replace(/'/g, '&#39;');
  }

  var NEWSLETTER_EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
  // JCA Mail turns the email left on the last screen (or typed in the form) into a code made only for
  // this customer (one use, also emailed). There is no fixed code: without an answer, no code is shown.
  var JCA_MAIL_SIGNUP_URL = 'https://mail.jcacreative.es/p/signup';
  var JCA_MAIL_STORE = 'zencomplex';

  /** Mirrors the API's discount-code shape check (letters, digits, _ - . #), max 64. */
  var PROMO_CODE_PATTERN = /^[\p{L}\p{N}_\-.#]{1,64}$/u;

  // Same strokes as snippets/zc-cod-icon.liquid, for markup built in JS.
  var ICON_CHECK = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M5 13l4 4L19 7"/></svg>';
  var ICON_PLUS = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" aria-hidden="true"><path d="M12 5v14M5 12h14"/></svg>';

  if (!customElements.get('zc-cod-checkout')) {
    class ZcCodCheckoutEl extends HTMLElement {
      connectedCallback() {
        var self = this;
        this.refs = {};
        this.querySelectorAll('[data-zc-cod-ref]').forEach(function (el) {
          self.refs[el.getAttribute('data-zc-cod-ref')] = el;
        });

        this.apiUrl = (this.dataset.apiUrl || '').replace(/\/+$/, '');
        this.storeKey = this.dataset.storeKey || '';
        this.exitOfferEnabled = this.dataset.exitOfferEnabled === 'true';

        this.variantId = null;
        this.quantity = 1;
        this.productTitle = '';
        this.productImage = '';
        this.productSubtitle = '';
        this.openedAt = 0;
        this.previouslyFocused = null;
        this.touched = {};
        this.eventId = '';

        this.session = null;
        this.sessionPending = false;
        this.exitOfferClaimed = false;
        this.exitOfferShown = false;
        this.upsellChecked = false;
        /** Applied discount code: { code, amountCents, cardOnly } — see applyPromo(). */
        this.promo = null;
        this.promoPending = false;
        /** Issued with a COD order; lets the confirmation screen save an email on it — see submitNewsletter(). */
        this.contactToken = null;
        this.lastLeadKey = '';
        this.isDemo = false;

        this.refs.panel.addEventListener('click', function (event) {
          if (event.target === self.refs.panel) self.close();
        });
        this.refs.panel.addEventListener('cancel', function (event) {
          event.preventDefault();
          self.close();
        });
        this._onKeyDown = function (event) {
          if (event.key === 'Escape' && self.refs.panel.open && !event.defaultPrevented) self.close();
        };
        document.addEventListener('keydown', this._onKeyDown);

        this.refs.closeBtn.addEventListener('click', function () { self.close(); });
        this.refs.continueBtn.addEventListener('click', function () { self.goToStep(2); });
        var paymentSection = this.querySelector('.zc-cod-section[data-step="2"]');
        if (paymentSection) {
          paymentSection.addEventListener('focusin', function () { self.sendLead(); });
          paymentSection.addEventListener('pointerdown', function () { self.sendLead(); });
        }
        this.refs.backBtn.addEventListener('click', function () { self.goToStep(1); });
        this.refs.exitOfferAccept.addEventListener('click', function () { self.acceptExitOffer(); });
        this.refs.exitOfferDecline.addEventListener('click', function () { self.declineExitOffer(); });
        this.refs.form.addEventListener('submit', function (event) { self.handleSubmit(event); });
        // Same as the Releasit form: the first character typed in any field
        // reports AddPaymentInfo, once per page load and with no parameters.
        this.refs.form.addEventListener('input', function (event) {
          if (self.paymentInfoSent || !event.target || !String(event.target.value || '').length) return;
          self.paymentInfoSent = true;
          self.firePixel('AddPaymentInfo');
        });

        this.refs.province.addEventListener('change', function () {
          self.touched.province = true;
          self.validateProvince();
          self.renderTotals();
          var surcharge = self.matchingSurcharge();
          self.refs.provinceShippingHint.hidden = !surcharge;
          if (surcharge) self.refs.provinceShippingHint.textContent = '+' + self.formatMoney(surcharge.cents) + ' envío';
        });

        this.refs.phonePrefix.addEventListener('change', function () {
          if (self.touched.phone) self.validateField('phone');
        });

        this.refs.zip.addEventListener('input', function () {
          var zip = self.refs.zip.value.trim();
          if (!ZIP_PATTERN.test(zip)) return;
          var province = PROVINCE_BY_ZIP_PREFIX[zip.slice(0, 2)];
          if (!province || self.refs.province.value === province) return;
          self.refs.province.value = province;
          self.refs.province.dispatchEvent(new Event('change', { bubbles: true }));
        });

        VALIDATED_FIELDS.forEach(function (ref) {
          var input = self.refs[ref];
          if (!input) return;
          input.addEventListener('blur', function () {
            self.touched[ref] = true;
            self.validateField(ref);
          });
          input.addEventListener('input', function () {
            if (self.touched[ref]) self.validateField(ref);
          });
        });

        this.initPromo();
        this.initNewsletter();
      }

      disconnectedCallback() {
        document.removeEventListener('keydown', this._onKeyDown);
        this.unlockScroll();
      }

      phoneRule() {
        return PHONE_RULES_BY_PREFIX[this.refs.phonePrefix.value] || DEFAULT_PHONE_RULE;
      }

      /** The phone as bare national digits — see normalizePhone(). */
      phoneDigits() {
        return normalizePhone(this.refs.phone.value, this.refs.phonePrefix.value, this.phoneRule());
      }

      // ---------------------------------------------------------------------
      // Discount code
      // ---------------------------------------------------------------------

      initPromo() {
        var self = this;
        if (!this.refs.promo) return;

        this.refs.promoToggle.addEventListener('click', function () {
          var open = self.refs.promoBody.hidden;
          self.refs.promoBody.hidden = !open;
          self.refs.promoToggle.setAttribute('aria-expanded', String(open));
          if (open) self.refs.promoInput.focus();
        });
        this.refs.promoApply.addEventListener('click', function () { self.applyPromo(); });
        this.refs.promoInput.addEventListener('keydown', function (event) {
          // Enter inside the order form would otherwise submit the ORDER.
          if (event.key !== 'Enter') return;
          event.preventDefault();
          self.applyPromo();
        });
        this.refs.promoInput.addEventListener('input', function () { self.hidePromoError(); });
        this.refs.promoRemove.addEventListener('click', function () {
          self.removePromo();
          self.refs.promoToggle.focus();
        });
        this.refs.form.addEventListener('change', function (event) {
          if (event.target && event.target.name === 'payment_method') self.renderPromo();
        });
      }

      selectedPaymentMethod() {
        var checked = this.refs.form.querySelector('input[name="payment_method"]:checked');
        return checked ? checked.value : 'cod';
      }

      /**
       * Asks JCA Pay what this code is worth on exactly this order (session
       * lines, upsell ticked or not, this shopper's phone for once-per-customer
       * codes). The answer is only a preview — placing the order re-checks it
       * from scratch server-side.
       */
      checkPromo(code) {
        var phone = this.phoneDigits();
        var body = {
          session_token: this.session.token,
          code: code,
          add_upsell: this.upsellChecked,
        };
        if (phone && this.phoneRule().pattern.test(phone)) body.phone = this.refs.phonePrefix.value + phone;

        return fetch(this.apiUrl + '/v1/discounts/check', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(body),
        }).then(function (response) {
          return response.json().catch(function () { return null; }).then(function (result) {
            if (!response.ok || !result || typeof result.valid !== 'boolean') {
              var error = new Error('discount check failed: ' + response.status);
              // 401 = the price session (15 min) expired while the form sat
              // open; 429 = too many tries. Anything else gets the generic
              // message — raw API wording isn't written for shoppers.
              error.publicMessage =
                response.status === 401
                  ? 'Ha pasado demasiado tiempo. Cierra el formulario y vuelve a abrirlo para aplicar tu código.'
                  : response.status === 429
                    ? 'Demasiados intentos. Espera un minuto y vuelve a probar.'
                    : '';
              throw error;
            }
            return result;
          });
        });
      }

      /** Each check gets a number; only the latest one may touch the UI, so a slow earlier answer can't overwrite a newer one. */
      nextPromoRequest() {
        this._promoRequest = (this._promoRequest || 0) + 1;
        return this._promoRequest;
      }

      applyPromo() {
        var self = this;
        var code = this.refs.promoInput.value.trim().toUpperCase();
        if (!code) {
          this.showPromoError('Escribe tu código.');
          this.refs.promoInput.focus();
          return;
        }
        // Same characters the API accepts; anything else can't be a real code.
        if (!PROMO_CODE_PATTERN.test(code)) {
          this.showPromoError('Este código no existe. Revisa que esté bien escrito.');
          this.refs.promoInput.classList.add('is-invalid');
          return;
        }
        if (!this.session) {
          this.showPromoError('Espera un momento, estamos calculando el precio de tu pedido.');
          return;
        }

        var request = this.nextPromoRequest();
        this.setPromoPending(true);
        this.checkPromo(code)
          .then(function (result) {
            if (request !== self._promoRequest) return;
            if (result.valid) {
              // One extra discount at a time (the API applies the same rule):
              // a code only replaces the exit offer when it's worth more.
              if (self.exitOfferClaimed && self.exitOfferCents() >= result.amount_cents) {
                self.showPromoError('Ya tienes aplicado un descuento mayor (' + self.session.exitOfferPercent + '%). Este código no lo mejora.');
                return;
              }
              self.exitOfferClaimed = false;
              self.promo = { code: result.code, amountCents: result.amount_cents, cardOnly: false };
            } else if (result.card_only) {
              self.promo = { code: result.code, amountCents: 0, cardOnly: true };
            } else {
              self.showPromoError(result.message);
              self.refs.promoInput.classList.add('is-invalid');
              return;
            }
            self.refs.promoInput.value = '';
            self.renderPromo();
            self.renderTotals();
          })
          .catch(function (error) {
            if (request !== self._promoRequest) return;
            console.error('[zc-cod-checkout] No se pudo comprobar el código:', error);
            self.showPromoError(error.publicMessage || 'No hemos podido comprobar el código. Inténtalo de nuevo en unos segundos.');
          })
          .finally(function () {
            if (request === self._promoRequest) self.setPromoPending(false);
          });
      }

      /**
       * The code's value depends on the order's contents (an order code also
       * discounts the upsell, and some codes don't combine with discounted
       * items), so it's re-checked whenever those change.
       */
      recheckPromo() {
        var self = this;
        if (!this.promo || this.promo.cardOnly || !this.session) return;
        var code = this.promo.code;
        var request = this.nextPromoRequest();
        this.setPromoPending(true);
        this.checkPromo(code)
          .then(function (result) {
            if (request !== self._promoRequest || !self.promo || self.promo.code !== code) return;
            if (result.valid) {
              self.promo.amountCents = result.amount_cents;
            } else {
              self.removePromo();
              self.showPromoError(result.message);
            }
          })
          .catch(function (error) {
            if (request !== self._promoRequest) return;
            // Can't confirm the new value, so don't show a stale one: drop
            // the code and let the shopper re-apply it.
            console.error('[zc-cod-checkout] No se pudo recalcular el código:', error);
            self.removePromo();
            self.showPromoError(error.publicMessage || 'No hemos podido recalcular tu código. Vuelve a aplicarlo.');
          })
          .finally(function () {
            if (request !== self._promoRequest) return;
            self.setPromoPending(false);
            self.renderPromo();
            self.renderTotals();
          });
      }

      removePromo() {
        this.promo = null;
        // Any check still in flight is now about a code that's gone.
        this.nextPromoRequest();
        if (this.promoPending) this.setPromoPending(false);
        this.hidePromoError();
        this.renderPromo();
        this.renderTotals();
      }

      openPromoBody() {
        this.refs.promoBody.hidden = false;
        this.refs.promoToggle.setAttribute('aria-expanded', 'true');
      }

      setPromoPending(pending) {
        this.promoPending = pending;
        this.refs.promoApply.disabled = pending;
        this.refs.promoApply.textContent = pending ? 'Comprobando…' : 'Aplicar';
        this.renderTotals();
      }

      showPromoError(message) {
        this.openPromoBody();
        this.refs.promoError.textContent = message;
        this.refs.promoError.hidden = false;
      }

      hidePromoError() {
        this.refs.promoError.hidden = true;
        this.refs.promoError.textContent = '';
        this.refs.promoInput.classList.remove('is-invalid');
      }

      renderPromo() {
        if (!this.refs.promo) return;
        var promo = this.promo;
        this.refs.promoApplied.hidden = !promo;
        this.refs.promoToggle.hidden = Boolean(promo);
        if (promo) {
          this.refs.promoBody.hidden = true;
          this.refs.promoAppliedCode.textContent = promo.code;
          this.refs.promoAppliedNote.textContent = promo.cardOnly
            ? this.selectedPaymentMethod() === 'card'
              ? 'Se aplicará al pagar con tarjeta.'
              : 'Este código solo vale pagando con tarjeta.'
            : 'Ahorras ' + this.formatMoney(promo.amountCents);
        } else {
          this.refs.promoToggle.setAttribute('aria-expanded', String(!this.refs.promoBody.hidden));
        }
      }

      /** Only what JCA Pay confirmed — a card-only code's value is Shopify checkout's to show. */
      promoCents() {
        return this.promo && !this.promo.cardOnly ? this.promo.amountCents : 0;
      }

      /**
       * Entry point: called from the "Haz tu pedido" button on the product
       * page. Reads the selected pack straight off the zc-bundles block's DOM
       * (same single variant at different quantities — 1, 2 or 4 bottles —
       * not a pack of distinct products), opens the drawer immediately, then
       * asks the API to price it for real.
       */
      openCheckout(trigger) {
        this.eventId = this.generateEventId();

        // The pack boxes (blocks/zc-bundles.liquid) of the same product the
        // button belongs to: each radio is 1, 2 or 4 units of one variant.
        var scope = (trigger && trigger.closest('.product-information, product-component, .shopify-section')) || document;
        var picker = scope.querySelector('[data-zc-bundles]') || document.querySelector('[data-zc-bundles]');
        var activeInput = picker ? picker.querySelector('.zc-bundle__input:checked') : null;
        var activeBox = activeInput ? activeInput.closest('.zc-bundle') : null;

        this.variantId = Number((picker && picker.dataset.variantId) || (trigger && trigger.dataset.variantId));
        // Each product has its own JCA Pay store key (its own pack prices —
        // see blocks/zc-cod-button.liquid); the theme setting is the fallback.
        this.activeStoreKey = (trigger && trigger.dataset.storeKey) || this.storeKey;
        // The add-on is switched per product (blocks/zc-cod-button.liquid):
        // Dropea rejects orders that mix suppliers, so a product whose
        // supplier cannot ship the add-on never offers it — whatever JCA Pay
        // answers for its store key.
        this.upsellAllowed = Boolean(trigger && trigger.dataset.upsellEnabled === 'true');
        this.activeUpsellLabel = (trigger && trigger.dataset.upsellShortLabel) || '';
        this.activeUpsellDescription = (trigger && trigger.dataset.upsellDescription) || '';
        this.quantity = Number(activeInput && activeInput.dataset.units) || 1;

        var titleEl = activeBox ? activeBox.querySelector('.zc-bundle__title-text') : null;
        var imageEl = activeBox ? activeBox.querySelector('.zc-bundle__media img') : null;
        var subEl = activeBox ? activeBox.querySelector('.zc-bundle__sub') : null;
        var productTitle = (trigger && trigger.dataset.productTitle) || '';
        var packTitle = titleEl ? titleEl.textContent.trim() : '';
        this.productTitle = packTitle ? productTitle + ' · ' + packTitle : productTitle;
        this.productImage = (imageEl && (imageEl.currentSrc || imageEl.src)) || (trigger && trigger.dataset.productImage) || '';
        // The pack's own line ("2 botes · Tratamiento de 2 meses") — repeated
        // here so the deal is still visible once the shopper has moved on
        // from the product page into the drawer, not just before.
        this.productSubtitle = (subEl && subEl.textContent.trim()) || '';

        if (!this.variantId) return;

        this.resetForm();
        this.setStep(1);
        this.renderSummary();
        this.show();
        this.openedAt = Date.now();

        this.createSession();
      }

      /**
       * Opens a checkout session with the API: the only place in this file
       * that ever mentions a price is here, and only to relay what the
       * server sent back. See lib/pricing (in the API) for why that split
       * exists.
       */
      createSession(isRenewal) {
        var self = this;
        this.session = null;
        this.sessionPending = true;
        this.sessionFailed = false;
        this.renderTotals();

        if (!this.apiUrl || !this.activeStoreKey) {
          console.warn('[zc-cod-checkout] Falta configurar la URL de la API o el identificador de tienda en Ajustes del tema → JCA Pay · Checkout.');
          this.sessionPending = false;
          this.showFormError('El pedido no se puede completar todavía: falta conectar este formulario con el sistema de pagos. Contacta con la tienda.');
          this.renderTotals();
          return;
        }

        var items = [{ variant_id: this.variantId, quantity: this.quantity }];
        var sessionRequest = { store_key: this.activeStoreKey, items: items };
        // Opening the form is reported to Meta twice, on purpose: by the pixel
        // (the InitiateCheckout below, when it is running) and by JCA Pay from
        // the server, with this same event id so Meta keeps one. Not on a
        // renewal: that is the same checkout.
        if (!isRenewal && this.eventId) {
          var openingAttribution = readAttribution();
          sessionRequest.meta = metaContext(this.eventId, openingAttribution);
          if (openingAttribution && openingAttribution.landingPage) {
            sessionRequest.landing_page = capAttribution(openingAttribution.landingPage, 500);
          }
        }

        fetchWithRetry(this.apiUrl + '/v1/sessions', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(sessionRequest),
        }, { attempts: 3, timeoutMs: 15000 })
          .then(function (response) {
            return response.json().catch(function () { return null; }).then(function (result) {
              if (!response.ok || !result || !result.session_token) {
                throw new Error((result && result.error && result.error.message) || 'session request failed: ' + response.status);
              }
              return result;
            });
          })
          .then(function (result) {
            self.session = {
              token: result.session_token,
              subtotalCents: result.subtotal_cents,
              catalogSubtotalCents: result.catalog_subtotal_cents,
              volumeDiscountBps: result.volume_discount_bps,
              shippingRules: {
                defaultCents: (result.rules && result.rules.shipping && result.rules.shipping.default_cents) || 0,
                surcharges: (result.rules && result.rules.shipping && result.rules.shipping.surcharges) || [],
              },
              exitOfferPercent: (result.rules && result.rules.exit_offer_percent) || 0,
              // The pack's free gift, when JCA Pay adds one to this order
              // (pricing_rules.free_gifts of the tenant). Display only: the
              // server decides it from the quantity and puts it in the order.
              gift: result.gift
                ? {
                    variantId: result.gift.variant_id,
                    quantity: result.gift.quantity || 1,
                    label: result.gift.label,
                    catalogPriceCents: result.gift.catalog_price_cents || 0,
                    imageUrl: result.gift.image_url,
                  }
                : null,
              upsell: result.upsell && self.upsellAllowed
                ? {
                    variantId: result.upsell.variant_id,
                    label: result.upsell.label,
                    priceCents: result.upsell.price_cents,
                    catalogPriceCents: result.upsell.catalog_price_cents,
                    imageUrl: result.upsell.image_url,
                  }
                : null,
            };
            self.renderUpsell();
            self.renderSummary();

            // A renewed session is the same checkout, not a new one.
            if (isRenewal) return;
            self.firePixel('InitiateCheckout', Object.assign({
              value: (self.session.subtotalCents + self.shippingCents()) / 100,
              currency: 'EUR',
              num_items: self.quantity,
            }, self.pixelContents()));
            // Opening the form is this store's "add to cart" (there is no
            // cart): reported too, as the Releasit form does.
            self.firePixel('AddToCart', Object.assign({
              value: (self.session.subtotalCents + self.shippingCents()) / 100,
              currency: 'EUR',
              num_items: self.quantity,
            }, self.pixelContents()));
          })
          .catch(function (error) {
            console.error('[zc-cod-checkout] No se pudo calcular el precio del pedido:', error);
            self.showFormError('No se pudo calcular el precio de tu pedido. Revisa tu conexión y vuelve a pulsar el botón: lo intentaremos de nuevo.');
            // Without a session the button stays disabled; re-enabling it lets
            // the shopper's next tap retry (see handleSubmit's no-session path).
            self.sessionFailed = true;
          })
          .finally(function () {
            self.sessionPending = false;
            self.renderTotals();
          });
      }

      show() {
        var panel = this.refs.panel;
        this.previouslyFocused = document.activeElement;
        this.lockScroll();
        panel.showModal();
        this.setAttribute('open', '');
      }

      lockScroll() {
        this._prevOverflow = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
      }

      unlockScroll() {
        document.body.style.overflow = this._prevOverflow || '';
      }

      /**
       * Every close attempt (X button, backdrop click, Escape) goes through
       * here first. The exit-intent offer hijacks the first one per page
       * load — everything after that (declining, or any later attempt)
       * closes for real.
       */
      close() {
        if (this.shouldShowExitOffer()) {
          this.showExitOffer();
          return;
        }
        this.reallyClose();
      }

      reallyClose() {
        var panel = this.refs.panel;
        if (!panel.open) return;

        // The store has no visible cart: a card checkout builds one from a
        // permalink (see submitCardOrder). Wipe it on every close so nothing
        // lingers hidden once the shopper walks away without paying.
        this.clearCartIdentity();

        this.unlockScroll();
        panel.close();
        this.removeAttribute('open');

        if (this.previouslyFocused && document.contains(this.previouslyFocused)) {
          this.previouslyFocused.focus();
        }
        this.previouslyFocused = null;
      }

      /**
       * Shopify's `cart` cookie IS the checkout's identity, not just a
       * pointer to its contents: `/cart/clear.js` empties the cart's line
       * items but leaves this cookie's value untouched, so an abandoned
       * checkout from an earlier attempt in this same browser — one where
       * the exit offer was claimed, say — gets silently RESUMED next time,
       * discount and all, even when the new attempt never asked for it.
       * Confirmed live: submitting again with exitOfferClaimed reset to
       * false still landed on the exact same checkout token and the exact
       * same discounted total as the claimed attempt before it. Deleting
       * the cookie (not just clearing its contents) forces Shopify to mint
       * a fresh cart — and therefore a fresh, uncontaminated checkout —
       * the next time one is needed.
       */
      clearCartIdentity() {
        document.cookie = 'cart=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/';
        fetch('/cart/clear.js', { method: 'POST', headers: { Accept: 'application/json' } }).catch(function () {});
      }

      shouldShowExitOffer() {
        return (
          this.exitOfferEnabled &&
          !this.exitOfferShown &&
          Boolean(this.session) &&
          // Someone who already typed a code isn't leaving over price.
          !this.promo &&
          this.dataset.currentStep !== '3' &&
          this.refs.exitOffer.hidden
        );
      }

      showExitOffer() {
        this.exitOfferShown = true;
        this.refs.formStage.hidden = true;
        this.refs.exitOffer.hidden = false;
        this.refs.exitOfferPercent.textContent = ((this.session && this.session.exitOfferPercent) || 0) + '%';
        var btn = this.refs.exitOffer.querySelector('button');
        if (btn) btn.focus();
      }

      acceptExitOffer() {
        this.exitOfferClaimed = true;
        this.refs.exitOffer.hidden = true;
        this.refs.formStage.hidden = false;
        this.renderTotals();
      }

      declineExitOffer() {
        this.refs.exitOffer.hidden = true;
        this.reallyClose();
      }

      goToStep(step) {
        if (step > 1 && !this.validateShippingStep()) return;
        if (step > 1) this.sendLead();
        this.setStep(step);
        this.refs.formStage.scrollTo({ top: 0, behavior: 'instant' });
      }

      setStep(step) {
        this.dataset.currentStep = String(step);
        Array.prototype.forEach.call(this.refs.stepIndicator.children, function (item) {
          if (!item.dataset || !item.dataset.step) return;
          var itemStep = Number(item.dataset.step);
          item.classList.toggle('is-active', itemStep === step);
          item.classList.toggle('is-done', itemStep < step);
        });
      }

      /** The error a field would show right now ('' when it is fine), without touching the page. */
      fieldMessage(ref) {
        var input = this.refs[ref];
        if (!input) return '';

        if (ref === 'phone') {
          var value = this.phoneDigits();
          var rule = this.phoneRule();
          return value === '' ? 'Escribe tu teléfono.' : !rule.pattern.test(value) ? rule.message : '';
        }

        var rules = FIELD_RULES[ref];
        if (!rules) return '';

        var val = input.value.trim();
        var msg = '';

        if (val === '') {
          msg = rules.required;
        } else if (rules.maxLength && val.length > rules.maxLength) {
          msg = rules.tooLong;
        } else if (rules.minLength && val.length < rules.minLength) {
          msg = rules.tooShort;
        } else if (rules.pattern && !rules.pattern.test(val)) {
          msg = rules.patternMessage;
        }
        return msg;
      }

      validateField(ref) {
        var input = this.refs[ref];
        if (!input) return true;
        var msg = this.fieldMessage(ref);
        this.setFieldError(input, this.refs[ref + 'Error'], msg);
        return msg === '';
      }

      /** Whether every shipping field is filled in correctly (checked silently: nothing is shown). */
      shippingComplete() {
        var self = this;
        return this.refs.province.value !== '' && VALIDATED_FIELDS.every(function (ref) { return self.fieldMessage(ref) === ''; });
      }

      /** What the order and the lead send about the shopper. The email only when typed and valid. */
      customerPayload() {
        var email = this.refs.orderEmail ? this.refs.orderEmail.value.trim() : '';
        var customer = {
          full_name: this.refs.fullName.value.trim(),
          phone: this.refs.phonePrefix.value + this.phoneDigits(),
          address1: this.refs.address.value.trim(),
          zip: this.refs.zip.value.trim(),
          city: this.refs.city.value.trim(),
          province: this.refs.province.value,
        };
        if (email && NEWSLETTER_EMAIL_PATTERN.test(email)) customer.email = email;
        return customer;
      }

      /**
       * Saves the shopper as a Shopify customer as soon as the shipping step is complete, whether or
       * not they end up buying (JCA Pay matches them by email and phone, and tags them until they order).
       * Fire and forget: never delays the form nor shows anything. Sent again only if the data changed.
       */
      sendLead() {
        if (!this.session || this.isDemo || !this.shippingComplete()) return;
        var customer = this.customerPayload();
        var key = JSON.stringify(customer);
        if (key === this.lastLeadKey) return;
        this.lastLeadKey = key;
        try {
          fetch(this.apiUrl + '/v1/leads', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ session_token: this.session.token, customer: customer }),
            keepalive: true,
          }).catch(function () {});
        } catch (e) {}
      }

      validateProvince() {
        var province = this.refs.province;
        var message = province.value === '' ? 'Selecciona tu provincia.' : '';
        this.setFieldError(province, this.refs.provinceError, message);
        return message === '';
      }

      setFieldError(input, errorEl, message) {
        input.classList.toggle('is-invalid', message !== '');
        input.setAttribute('aria-invalid', String(message !== ''));
        if (errorEl) {
          errorEl.textContent = message;
          errorEl.hidden = message === '';
        }
      }

      validateShippingStep() {
        var self = this;
        var firstInvalid = null;

        VALIDATED_FIELDS.forEach(function (ref) {
          self.touched[ref] = true;
          if (!self.validateField(ref) && !firstInvalid) firstInvalid = self.refs[ref];
        });

        this.touched.province = true;
        if (!this.validateProvince() && !firstInvalid) firstInvalid = this.refs.province;

        if (firstInvalid) {
          this.setStep(1);
          firstInvalid.focus();
        }

        return firstInvalid === null;
      }

      handleSubmit(event) {
        event.preventDefault();
        this.hideFormError();

        if (this.refs.honeypot.value !== '') return;

        if (Date.now() - this.openedAt < MIN_FILL_TIME_MS) {
          this.showFormError('Espera un momento antes de enviar el formulario.');
          return;
        }

        if (!this.validateShippingStep()) return;

        if (this.promoPending) return;

        var paymentMethod = this.selectedPaymentMethod();

        if (paymentMethod === 'card') {
          this.submitCardOrder();
          return;
        }

        if (this.promo && this.promo.cardOnly) {
          this.showFormError('El código ' + this.promo.code + ' solo vale pagando con tarjeta. Elige tarjeta o quita el código para pagar al recibir.');
          return;
        }

        // The submit button is disabled without a price session, but Enter
        // in a field still submits: never let that reach submitCodOrder()
        // with no session (it would throw and leave the form stuck).
        if (!this.session) {
          if (!this.sessionPending) this.createSession(true);
          this.showFormError('Estamos calculando el precio de tu pedido. Vuelve a pulsar en un momento.');
          return;
        }

        this.submitCodOrder();
      }

      /**
       * Shopify pre-fills checkout's shipping-address fields from
       * `checkout[shipping_address][x]` query params, but only reliably on
       * the cart permalink route (`/cart/<variant>:<qty>?...`) — not just any
       * bare `/checkout` visit — see
       * https://shopify.dev/docs/apps/build/checkout/create-cart-permalinks.
       * There's no `email` to carry over (this form never asks for one), so
       * the contact section stays blank; every shipping field this form does
       * collect does not need retyping.
       */
      buildCheckoutUrl(cartPart) {
        var fullName = this.refs.fullName.value.trim();
        var spaceIndex = fullName.indexOf(' ');
        var firstName = spaceIndex === -1 ? fullName : fullName.slice(0, spaceIndex);
        var lastName = spaceIndex === -1 ? '' : fullName.slice(spaceIndex + 1).trim();

        var params = new URLSearchParams();
        if (firstName) params.set('checkout[shipping_address][first_name]', firstName);
        if (lastName) params.set('checkout[shipping_address][last_name]', lastName);
        params.set('checkout[shipping_address][address1]', this.refs.address.value.trim());
        params.set('checkout[shipping_address][city]', this.refs.city.value.trim());
        params.set('checkout[shipping_address][zip]', this.refs.zip.value.trim());
        params.set('checkout[shipping_address][province]', this.refs.province.value);
        params.set('checkout[shipping_address][country]', 'Spain');
        var phone = this.phoneDigits();
        if (phone) params.set('checkout[shipping_address][phone]', this.refs.phonePrefix.value + phone);

        return '/cart/' + cartPart + '?' + params.toString();
      }

      /**
       * Card payments go through Shopify's own real checkout — never through
       * JCA Pay — so no card data ever touches this form. The pack price the
       * shopper saw is reproduced there by real, matching Shopify automatic
       * discounts (Pack 2/4 per product), which apply on their own for
       * whatever quantity ends up in the cart. The checkout URL is built as
       * a fresh permalink so the shipping fields the shopper already typed
       * come along with it.
       */
      submitCardOrder() {
        var submitButton = this.refs.submitButton;
        var submitLabel = this.refs.submitLabel;
        submitButton.disabled = true;
        if (submitLabel) submitLabel.textContent = 'Redirigiendo…';

        this.firePixel('AddPaymentInfo', Object.assign({
          value: this.totalCents() / 100,
          currency: 'EUR',
        }, this.pixelContents()));

        var self = this;
        var upsell = this.session && this.session.upsell;
        var cartPart = this.variantId + ':' + this.quantity;
        if (this.upsellChecked && upsell) cartPart += ',' + upsell.variantId + ':1';
        var checkoutUrl = this.buildCheckoutUrl(cartPart);

        // The exit-offer is the one discount still applied by code (it is a
        // one-off retention offer tied to a Shopify discount code the
        // merchant creates directly, not a standing tenant-pricing discount
        // syncTenantDiscounts manages) — a deliberate decision the shopper
        // just made, so it takes priority over just landing on plain checkout.
        // A code the shopper typed goes the same way. The two never coexist
        // (see applyPromo), so there's only ever one code to hand over.
        var exitOfferCode = this.dataset.exitOfferDiscountCode;
        var discountCode = this.promo ? this.promo.code : this.exitOfferClaimed ? exitOfferCode : '';
        if (discountCode) {
          checkoutUrl = '/discount/' + encodeURIComponent(discountCode) + '?redirect=' + encodeURIComponent(checkoutUrl);
        }

        // One last clear right before handing off to Shopify: whatever this
        // attempt decided (with or without the exit offer), it must land on
        // a checkout built fresh from exactly that decision, never one
        // resumed from an earlier attempt — see clearCartIdentity's comment.
        this.clearCartIdentity();
        window.location.href = checkoutUrl;
      }

      submitCodOrder() {
        var self = this;
        var submitButton = this.refs.submitButton;
        var submitLabel = this.refs.submitLabel;

        if (!this.session) {
          this.showFormError('Todavía se está calculando el precio de tu pedido. Espera un momento e inténtalo de nuevo.');
          return;
        }

        submitButton.disabled = true;
        var originalLabel = submitLabel ? submitLabel.textContent : null;
        if (submitLabel) submitLabel.textContent = 'Enviando pedido…';

        var attribution = readAttribution();

        var payload = {
          session_token: this.session.token,
          claim_exit_offer: this.exitOfferClaimed,
          add_upsell: this.upsellChecked && Boolean(this.session.upsell),
          customer: this.customerPayload(),
        };
        if (this.promo && !this.promo.cardOnly) payload.discount_code = this.promo.code;
        // Fixed here: the Purchase below must carry exactly the id the server
        // reports with, or Meta cannot merge the two copies.
        var purchaseEventId = this.eventId;
        if (purchaseEventId) payload.meta = metaContext(purchaseEventId, attribution);
        if (attribution) {
          payload.attribution = {
            utm_source: capAttribution(attribution.utm_source, 150),
            utm_medium: capAttribution(attribution.utm_medium, 150),
            utm_campaign: capAttribution(attribution.utm_campaign, 150),
            utm_term: capAttribution(attribution.utm_term, 150),
            utm_content: capAttribution(attribution.utm_content, 150),
            landing_page: capAttribution(attribution.landingPage, 500),
            referrer: capAttribution(attribution.referrer, 500),
          };
        }

        fetchWithRetry(this.apiUrl + '/v1/orders', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
        }, {
          attempts: 4,
          timeoutMs: 30000,
          retryStatuses: [409],
          onAttempt: function (attempt) {
            if (attempt > 1 && submitLabel) submitLabel.textContent = 'Confirmando tu pedido…';
          },
        })
          .then(function (response) {
            return response.json().catch(function () { return null; }).then(function (result) {
              if (!response.ok || !result || !result.success || !result.status_page_url) {
                var error = new Error('order request failed: ' + response.status);
                error.code = result && result.error && result.error.code;
                error.publicMessage = result && result.error && result.error.message;
                throw error;
              }
              return result;
            });
          })
          .then(function (result) {
            self.firePixel('Purchase', Object.assign({
              value: result.total_cents / 100,
              currency: result.currency || 'EUR',
            }, self.pixelContents()), purchaseEventId);
            self.showConfirmation(result);
          })
          .catch(function (error) {
            console.error('[zc-cod-checkout] No se pudo crear el pedido:', error);
            submitButton.disabled = false;
            if (submitLabel && originalLabel) submitLabel.textContent = originalLabel;

            // The code stopped applying between preview and order (used up,
            // expired, redeemed elsewhere): drop it so the new total shows,
            // and say why — the shopper confirms again knowingly.
            if (error.code === 'DISCOUNT_CODE_REJECTED') {
              self.removePromo();
              self.showPromoError(error.publicMessage);
              self.showFormError('Tu código ya no es válido: ' + error.publicMessage + ' Revisa el nuevo total y vuelve a confirmar.');
              return;
            }
            // The price session (15 min) ran out while the form was being
            // filled in: fetch a fresh one — everything typed stays — and
            // let the shopper confirm again instead of hitting a dead end.
            if (error.code === 'UNAUTHORIZED') {
              self.createSession(true);
              self.showFormError('Hemos actualizado el precio porque pasó un rato. Pulsa de nuevo para confirmar tu pedido.');
              return;
            }
            self.showFormError(
              error.code === 'CONFLICT' || error.code === 'RATE_LIMITED'
                ? error.publicMessage
                : 'No se pudo completar tu pedido. Inténtalo de nuevo en unos segundos.'
            );
          });
      }

      showFormError(message) {
        this.refs.formError.textContent = message;
        this.refs.formError.hidden = false;
      }

      hideFormError() {
        this.refs.formError.hidden = true;
        this.refs.formError.textContent = '';
      }

      /**
       * COD order created: show our own confirmation (amount to pay in cash,
       * newsletter offer) inside the drawer instead of jumping straight to
       * Shopify's order status page — that page is Shopify checkout, not the
       * theme, so nothing can be added to it from here. It stays one tap
       * away via the "Ver el estado de mi pedido" link.
       */
      showConfirmation(result) {
        // The order was created server-side from the session, not from the
        // Shopify cart — drop any cart left from an earlier card attempt.
        this.clearCartIdentity();

        var nameParts = this.refs.fullName.value.trim().split(/\s+/).filter(Boolean);
        var firstName = nameParts[0] || '';
        var title = this.refs.confirmationTitle;
        if (title) title.textContent = firstName ? '¡Gracias, ' + firstName + '!' : title.dataset.defaultTitle;
        if (this.refs.confirmationTotal) this.refs.confirmationTotal.textContent = this.formatMoney(result.total_cents);
        if (this.refs.statusLink) this.refs.statusLink.href = result.status_page_url;
        this.renderConfirmationItems();

        var orderName = result.order_name || '';
        if (this.refs.confirmationOrder) {
          this.refs.confirmationOrderName.textContent = orderName;
          this.refs.confirmationOrder.hidden = !orderName;
        }
        this.updateWhatsappLink(firstName, orderName, result.total_cents);
        this.contactToken = result.contact_token || null;

        this.refs.formStage.hidden = true;
        this.refs.exitOffer.hidden = true;
        this.refs.confirmation.hidden = false;
        this.setStep(3);
        this.refs.confirmation.scrollTop = 0;

        // Restart the entrance animation even if the class was already set.
        this.refs.confirmation.classList.remove('is-celebrating');
        void this.refs.confirmation.offsetWidth;
        this.refs.confirmation.classList.add('is-celebrating');
        this.launchConfetti();

        // Focus the heading, not the email field: on phones that would pop
        // the keyboard up over the confirmation before it's even been read.
        if (title) title.focus({ preventScroll: true });

        // The email typed in the form (optional) gets its code at once: no second question.
        var typed = this.refs.orderEmail ? this.refs.orderEmail.value.trim() : '';
        if (typed && this.refs.newsletterEmail && NEWSLETTER_EMAIL_PATTERN.test(typed)) {
          this.refs.newsletterEmail.value = typed;
          this.submitNewsletter();
        }
      }

      /**
       * Pre-fills the store's WhatsApp with who is writing and which order,
       * so support can find it without asking (the chat already shows the
       * shopper's own number).
       */
      updateWhatsappLink(firstName, orderName, totalCents) {
        var link = this.refs.whatsappLink;
        if (!link) return;
        var message =
          'Hola' + (firstName ? ', soy ' + firstName : '') + '. ' +
          'Acabo de hacer ' + (orderName ? 'el pedido ' + orderName : 'un pedido') +
          ' de ' + this.formatMoney(totalCents) +
          ' y me gustaría tener más información sobre el envío.';
        link.href = 'https://wa.me/' + link.dataset.whatsappNumber + '?text=' + encodeURIComponent(message);
      }

      /** What was just ordered, as a short visual recap above the amount due. */
      renderConfirmationItems() {
        var container = this.refs.confirmationItems;
        if (!container) return;
        var html = '';
        if (this.productTitle) {
          html += this.confirmationItemHTML(
            this.productImage,
            this.productTitle,
            this.quantity + (this.quantity === 1 ? ' unidad' : ' unidades')
          );
        }
        var upsell = this.session && this.session.upsell;
        if (this.upsellChecked && upsell) {
          html += this.confirmationItemHTML(upsell.imageUrl, this.upsellShortLabel(), '1 unidad');
        }
        var gift = this.session && this.session.gift;
        if (gift) {
          html += this.confirmationItemHTML(gift.imageUrl, gift.label, 'Regalo · ' + gift.quantity + (gift.quantity === 1 ? ' unidad' : ' unidades'));
        }
        container.innerHTML = html;
      }

      confirmationItemHTML(image, name, detail) {
        return (
          '<div class="zc-cod-receipt__item">' +
          (image ? '<img src="' + escapeHtml(image) + '" width="48" height="48" alt="">' : '') +
          '<span class="zc-cod-receipt__item-copy"><strong>' + escapeHtml(name) + '</strong><small>' + escapeHtml(detail) + '</small></span>' +
          '</div>'
        );
      }

      /** Short burst of brand-coloured confetti over the confirmation. */
      launchConfetti() {
        var container = this.refs.confetti;
        if (!container) return;
        if (window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

        var colors = ['#0A7850', '#064E3B', '#A7F3D0', '#FBBF24', '#F59E0B', '#FFFFFF'];
        var fragment = document.createDocumentFragment();
        for (var i = 0; i < 70; i++) {
          var piece = document.createElement('span');
          var isRound = Math.random() < 0.3;
          var size = 6 + Math.random() * 6;
          piece.className = 'zc-cod-confetti__piece';
          piece.style.setProperty('--x', Math.random() * 100 + '%');
          piece.style.setProperty('--w', size + 'px');
          piece.style.setProperty('--h', (isRound ? size : size * 1.6) + 'px');
          piece.style.setProperty('--r', isRound ? '50%' : '2px');
          piece.style.setProperty('--c', colors[i % colors.length]);
          piece.style.setProperty('--d', 1.8 + Math.random() * 1.4 + 's');
          piece.style.setProperty('--delay', Math.random() * 0.5 + 's');
          piece.style.setProperty('--drift', (Math.random() - 0.5) * 160 + 'px');
          piece.style.setProperty('--spin', (Math.random() < 0.5 ? -1 : 1) * (360 + Math.random() * 540) + 'deg');
          fragment.appendChild(piece);
        }
        container.replaceChildren(fragment);
        clearTimeout(this._confettiTimer);
        this._confettiTimer = setTimeout(function () { container.replaceChildren(); }, 3800);
      }

      resetConfirmation() {
        var title = this.refs.confirmationTitle;
        if (title) title.textContent = title.dataset.defaultTitle;
        this.refs.confirmation.classList.remove('is-celebrating');
        if (this.refs.confetti) this.refs.confetti.replaceChildren();
        if (this.refs.confirmationItems) this.refs.confirmationItems.replaceChildren();
        if (!this.refs.newsletter) return;
        this.refs.newsletterForm.reset();
        this.refs.newsletterEmail.classList.remove('is-invalid');
        this.refs.newsletterError.hidden = true;
        this.refs.newsletterSubmit.disabled = false;
        this.refs.newsletterSubmit.textContent = this.newsletterButtonLabel;
        this.refs.newsletterSuccess.hidden = true;
        this.refs.newsletterSuccess.querySelector('.zc-cod-nl__success-note').textContent = this.newsletterSuccessNote;
        this.refs.newsletterCode.textContent = '';
        var resetCoupon = this.refs.newsletterSuccess.querySelector('.zc-cod-coupon');
        if (resetCoupon) resetCoupon.hidden = false;
        var resetTitle = this.refs.newsletterSuccess.querySelector('.zc-cod-nl__success-title');
        if (resetTitle && resetTitle.lastChild && this.newsletterTitle !== undefined) resetTitle.lastChild.textContent = this.newsletterTitle;
        this.refs.newsletter.querySelector('.zc-cod-nl__signup').hidden = false;
      }

      initNewsletter() {
        if (!this.refs.newsletterForm) return;
        var self = this;
        this.newsletterButtonLabel = this.refs.newsletterSubmit.textContent;
        this.newsletterSuccessNote = this.refs.newsletterSuccess.querySelector('.zc-cod-nl__success-note').textContent;
        this.refs.newsletterForm.addEventListener('submit', function (event) {
          event.preventDefault();
          self.submitNewsletter();
        });
        this.refs.newsletterEmail.addEventListener('input', function () {
          self.refs.newsletterEmail.classList.remove('is-invalid');
          self.refs.newsletterError.hidden = true;
        });
        this.refs.newsletterCopy.addEventListener('click', function () { self.copyNewsletterCode(); });
      }

      /**
       * Saves the email on THIS order's own Shopify customer record (the one
       * JCA Pay created or matched by phone), subscribed to email marketing
       * and tagged "newsletter" — and on the order itself, so Shopify's
       * notifications reach it. Goes through JCA Pay with the order's
       * contact token rather than Shopify's storefront customer form, which
       * sits behind a bot check and would create a second, unlinked customer.
       */
      submitNewsletter() {
        var self = this;
        var email = this.refs.newsletterEmail;
        var button = this.refs.newsletterSubmit;
        var value = email.value.trim();

        if (!NEWSLETTER_EMAIL_PATTERN.test(value)) {
          email.classList.add('is-invalid');
          this.showNewsletterError('Escribe un email válido, por ejemplo nombre@gmail.com.');
          email.focus();
          return;
        }

        // Design preview (#zc-confirmacion-demo): there is no real order to save it on.
        if (this.isDemo) {
          this.showNewsletterSuccess(null, { code: 'TU-CODIGO-PERSONAL', expires: '' });
          return;
        }
        if (!this.contactToken) {
          this.showNewsletterError('No hemos podido guardar tu email. Escríbenos por WhatsApp y te damos tu código.');
          return;
        }

        button.disabled = true;
        button.textContent = 'Un momento…';
        this.refs.newsletterError.hidden = true;

        fetch(this.apiUrl + '/v1/orders/contact', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ contact_token: this.contactToken, email: value }),
        })
          .then(function (response) {
            // The token only lives a couple of hours: past that, the shopper
            // bought anyway — hand over the code rather than a dead end.
            if (response.status === 401) {
              return self.requestPersonalCode(value).then(function (personal) {
                self.showNewsletterSuccess(null, personal);
              });
            }
            if (!response.ok) throw new Error('contact request failed: ' + response.status);
            self.firePixel('Lead', { content_name: 'Newsletter post-compra' });
            return self.requestPersonalCode(value).then(function (personal) { self.showNewsletterSuccess(null, personal); });
          })
          .catch(function (error) {
            console.error('[zc-cod-checkout] No se pudo guardar el email:', error);
            self.showNewsletterError('No hemos podido guardar tu email. Inténtalo de nuevo en unos segundos.');
            button.disabled = false;
            button.textContent = self.newsletterButtonLabel;
          });
      }

      /**
       * Asks JCA Mail for a code made only for this customer, checked against this order. Resolves
       * { code, expires } or null (not configured, no answer in time…), never rejects: the fixed
       * code is the fallback, so the shopper always leaves with a code.
       */
      requestPersonalCode(email) {
        var orderName = this.refs.confirmationOrderName ? this.refs.confirmationOrderName.textContent.trim() : '';
        if (!orderName || typeof fetch !== 'function') return Promise.resolve(null);
        var firstName = this.refs.fullName ? this.refs.fullName.value.trim().split(/\s+/)[0] || '' : '';
        // The order's phone proves the request comes from the buyer: an order placed without an
        // email gives its code only to whoever knows that phone.
        var phone = this.refs.phone && this.refs.phonePrefix ? this.refs.phonePrefix.value + this.phoneDigits() : '';
        var controller = typeof AbortController === 'function' ? new AbortController() : null;
        var timer = controller ? setTimeout(function () { controller.abort(); }, 15000) : null;
        // A plain string body goes as text/plain: a simple request, no preflight and no cookies.
        return fetch(JCA_MAIL_SIGNUP_URL, {
          method: 'POST',
          body: JSON.stringify({ store: JCA_MAIL_STORE, email: email, order: orderName, firstName: firstName, phone: phone }),
          signal: controller ? controller.signal : undefined,
        })
          .then(function (response) { return response.ok ? response.json() : null; })
          .then(function (data) { return data && data.ok && data.code ? { code: String(data.code), expires: String(data.expires || '') } : null; })
          .catch(function () { return null; })
          .then(function (result) {
            if (timer) clearTimeout(timer);
            return result;
          });
      }

      showNewsletterError(message) {
        this.refs.newsletterError.textContent = message;
        this.refs.newsletterError.hidden = false;
      }

      /**
       * The code is only ever the one JCA Mail made for this shopper (there is no fixed code). Without
       * one in time, the box is hidden and the shopper is told how to get it — never a made-up code.
       */
      showNewsletterSuccess(note, personal) {
        this.refs.newsletter.querySelector('.zc-cod-nl__signup').hidden = true;
        this.refs.newsletterSuccess.hidden = false;
        var coupon = this.refs.newsletterSuccess.querySelector('.zc-cod-coupon');
        var title = this.refs.newsletterSuccess.querySelector('.zc-cod-nl__success-title');
        if (this.newsletterTitle === undefined && title) this.newsletterTitle = title.lastChild ? title.lastChild.textContent : '';
        if (personal) {
          this.refs.newsletterCode.textContent = personal.code;
          if (coupon) coupon.hidden = false;
          if (title && title.lastChild) title.lastChild.textContent = this.newsletterTitle;
          note = 'Es solo para ti: un solo uso' + (personal.expires ? ', válido hasta el ' + personal.expires : '') + '. Te lo acabamos de enviar también por email.';
        } else {
          if (coupon) coupon.hidden = true;
          if (title && title.lastChild) title.lastChild.textContent = '¡Ya estás dentro!';
          note = 'Ahora mismo no podemos enseñarte tu código. Si en unos minutos no te llega por email, escríbenos por WhatsApp y te lo damos.';
        }
        if (note) this.refs.newsletterSuccess.querySelector('.zc-cod-nl__success-note').textContent = note;
        this.refs.newsletterCopy.focus({ preventScroll: true });
      }

      copyNewsletterCode() {
        var button = this.refs.newsletterCopy;
        var code = this.refs.newsletterCode.textContent.trim();
        var done = function () {
          button.textContent = '¡Copiado!';
          clearTimeout(button._resetTimer);
          button._resetTimer = setTimeout(function () { button.textContent = 'Copiar'; }, 2000);
        };
        if (navigator.clipboard && navigator.clipboard.writeText) {
          navigator.clipboard.writeText(code).then(done).catch(function () {});
          return;
        }
        var range = document.createRange();
        range.selectNodeContents(this.refs.newsletterCode);
        var selection = window.getSelection();
        selection.removeAllRanges();
        selection.addRange(range);
      }

      resetForm() {
        // Every fresh open starts from a clean cart/checkout identity too
        // (see clearCartIdentity's doc comment) — covers the case where the
        // shopper reaches this point without ever going through
        // reallyClose(), e.g. hitting the browser's back button after
        // abandoning a card checkout.
        this.clearCartIdentity();

        this.refs.form.reset();
        this.refs.formStage.hidden = false;
        this.refs.confirmation.hidden = true;
        this.resetConfirmation();
        this.refs.exitOffer.hidden = true;
        this.session = null;
        this.sessionPending = false;
        this.exitOfferClaimed = false;
        // exitOfferShown is deliberately NOT reset here — the offer is
        // capped at once per page load, even across several opens/closes
        // of the form. Only a real reload shows it again.
        this.upsellChecked = false;
        this.renderUpsell();
        this.promo = null;
        this.promoPending = false;
        this.nextPromoRequest();
        if (this.refs.promo) {
          this.refs.promoBody.hidden = true;
          this.refs.promoApply.disabled = false;
          this.refs.promoApply.textContent = 'Aplicar';
          this.hidePromoError();
          this.renderPromo();
        }
        this.contactToken = null;
        this.lastLeadKey = '';
        this.isDemo = false;
        this.hideFormError();
        this.touched = {};
        var self = this;
        VALIDATED_FIELDS.forEach(function (ref) {
          self.setFieldError(self.refs[ref], self.refs[ref + 'Error'], '');
        });
        this.setFieldError(this.refs.province, this.refs.provinceError, '');
        this.refs.provinceShippingHint.hidden = true;
      }

      matchingSurcharge() {
        var province = this.refs.province.value;
        if (!province || !this.session) return undefined;
        return this.session.shippingRules.surcharges.filter(function (s) {
          return s.province.toLowerCase() === province.toLowerCase();
        })[0];
      }

      shippingCents() {
        if (!this.session) return 0;
        var surcharge = this.matchingSurcharge();
        return surcharge ? surcharge.cents : this.session.shippingRules.defaultCents;
      }

      /**
       * The exit-offer code is an order-wide Shopify discount (see jca-pay-api's
       * discount-sync notes) — a code discount can only combine with the
       * automatic volume discount if both are the same "order" class,
       * so it necessarily also takes 10% off the mask upsell line when
       * that's in the cart, not just the main product. Mirrored here
       * (rather than carving the upsell out) so this total matches what
       * Shopify's checkout actually charges. Shopify floors each line's
       * per-unit discount before multiplying by quantity (proven via
       * live checkout tests — see money.ts), so that's replicated here
       * too rather than rounding the combined total in one step.
       */
      exitOfferCents() {
        if (!this.exitOfferClaimed || !this.session) return 0;
        var percent = this.session.exitOfferPercent;
        var mainUnitCents = Math.floor(this.session.subtotalCents / this.quantity);
        var discount = Math.floor((mainUnitCents * percent) / 100) * this.quantity;
        if (this.upsellChecked && this.session.upsell) {
          discount += Math.floor((this.session.upsell.priceCents * percent) / 100);
        }
        return discount;
      }

      /** Short product name for the summary and receipt rows — the offer card keeps the longer sales headline from JCA Pay. */
      upsellShortLabel() {
        return this.activeUpsellLabel || this.dataset.upsellShortLabel || (this.session && this.session.upsell && this.session.upsell.label) || '';
      }

      /**
       * The amount charged for the upsell if ticked — always the tenant's
       * fixed price from the session response, never the catalogue price.
       * The API re-verifies this independently at order time; this is only
       * for display.
       */
      upsellCents() {
        if (!this.upsellChecked || !this.session || !this.session.upsell) return 0;
        return this.session.upsell.priceCents;
      }

      totalCents() {
        if (!this.session) return 0;
        return this.session.subtotalCents - this.exitOfferCents() - this.promoCents() + this.shippingCents() + this.upsellCents();
      }

      /**
       * Renders the order-bump checkbox card into the fixed placeholder in
       * the summary aside (see zc-cod-drawer.liquid). Built dynamically,
       * like renderSummary() does for the cart items, because whether it
       * exists at all — and its label/price — only becomes known once the
       * session responds.
       */
      renderUpsell() {
        var card = this.refs.upsellCard;
        if (!card) return;

        var upsell = this.session && this.session.upsell;
        // The store has one add-on product for every checkout: never offer
        // it as an extra when it is the very product being bought.
        if (upsell && upsell.variantId === this.variantId) {
          this.session.upsell = null;
          upsell = null;
        }
        if (!upsell) {
          card.hidden = true;
          card.innerHTML = '';
          return;
        }

        var self = this;
        var hasDiscount = upsell.catalogPriceCents > upsell.priceCents;
        var savingsPercent = hasDiscount ? Math.round((1 - upsell.priceCents / upsell.catalogPriceCents) * 100) : 0;
        var price = this.formatMoney(upsell.priceCents);
        card.hidden = false;
        // Compact single row (image · copy · button): it sits above the
        // address form on phones, so it must not push the form off screen.
        card.innerHTML =
          '<label class="zc-cod-upsell__label">' +
          '<input type="checkbox" class="zc-cod-upsell__input" data-zc-cod-ref="upsellCheckbox"' + (this.upsellChecked ? ' checked' : '') + '>' +
          '<span class="zc-cod-upsell__ribbon"><b aria-hidden="true">★</b> Solo en este pedido</span>' +
          '<span class="zc-cod-upsell__main">' +
          (upsell.imageUrl
            ? '<span class="zc-cod-upsell__media">' +
              '<img class="zc-cod-upsell__image" src="' + escapeHtml(upsell.imageUrl) + '" width="56" height="56" alt="" loading="lazy">' +
              (savingsPercent > 0 ? '<span class="zc-cod-upsell__off">-' + savingsPercent + '%</span>' : '') +
              '</span>'
            : '') +
          '<span class="zc-cod-upsell__copy">' +
          '<strong class="zc-cod-upsell__title">' + escapeHtml(upsell.label) + '</strong>' +
          (this.activeUpsellDescription ? '<span class="zc-cod-upsell__desc">' + escapeHtml(this.activeUpsellDescription) + '</span>' : '') +
          '<span class="zc-cod-upsell__price">' +
          (hasDiscount ? '<span class="zc-cod-upsell__price-old">' + this.formatMoney(upsell.catalogPriceCents) + '</span>' : '') +
          '<span class="zc-cod-upsell__price-new">' + price + '</span>' +
          '</span>' +
          // Shipping is priced per order (see shippingCents()), so adding
          // this never changes it. Swapped for the "remove" hint once added.
          '<span class="zc-cod-upsell__perk">' + ICON_CHECK + 'Sin gastos de envío</span>' +
          '<span class="zc-cod-upsell__remove">Toca para quitarlo</span>' +
          '</span>' +
          '<span class="zc-cod-upsell__cta" aria-hidden="true">' +
          '<span class="zc-cod-upsell__cta-add">' + ICON_PLUS + 'Añadir</span>' +
          '<span class="zc-cod-upsell__cta-added">' + ICON_CHECK + 'Añadido</span>' +
          '</span>' +
          '</span>' +
          '</label>';

        this.refs.upsellCheckbox = card.querySelector('[data-zc-cod-ref="upsellCheckbox"]');
        this.refs.upsellCheckbox.addEventListener('change', function () {
          self.upsellChecked = self.refs.upsellCheckbox.checked;
          self.renderTotals();
          self.recheckPromo();
        });
      }

      renderSummary() {
        // No "× N" suffix needed here :
        // the title already says the pack ("Cortisol Balance · 120 cápsulas"), so
        // appending the raw quantity would just repeat what it already says.
        var label = escapeHtml(this.productTitle);
        var subtitle = this.productSubtitle
          ? '<p class="zc-cod-summary__item-subtitle">' + escapeHtml(this.productSubtitle) + '</p>'
          : '';
        this.refs.summaryItems.innerHTML =
          '<div class="zc-cod-summary__item">' +
          '<img class="zc-cod-summary__image" src="' + escapeHtml(this.productImage || '') + '" width="56" height="56" alt="" loading="lazy">' +
          '<div class="zc-cod-summary__item-copy">' +
          '<p class="zc-cod-summary__item-title">' + label + '</p>' +
          subtitle +
          '</div></div>' +
          this.giftItemHTML();
        this.renderTotals();
      }

      /**
       * The free gift as its own line under the product, so the shopper
       * sees it is in the order before confirming. Only ever what the
       * session answered — the product page announces it, JCA Pay grants it.
       */
      giftItemHTML() {
        var gift = this.session && this.session.gift;
        if (!gift) return '';
        return (
          '<div class="zc-cod-summary__item zc-cod-summary__item--gift">' +
          (gift.imageUrl
            ? '<img class="zc-cod-summary__image" src="' + escapeHtml(gift.imageUrl) + '" width="56" height="56" alt="" loading="lazy">'
            : '<span class="zc-cod-summary__image" aria-hidden="true"></span>') +
          '<div class="zc-cod-summary__item-copy">' +
          '<p class="zc-cod-summary__gift-tag">Regalo</p>' +
          '<p class="zc-cod-summary__item-title">' + escapeHtml(gift.label) + '</p>' +
          '</div>' +
          '<p class="zc-cod-summary__gift-price"><strong>Gratis</strong>' +
          (gift.catalogPriceCents > 0 ? '<s>' + this.formatMoney(gift.catalogPriceCents * gift.quantity) + '</s>' : '') +
          '</p>' +
          '</div>'
        );
      }

      renderTotals() {
        if (this.refs.submitButton) {
          // Never submittable mid-recheck: the total on the button might be about to change.
          this.refs.submitButton.disabled = this.sessionPending || this.promoPending || (!this.session && !this.sessionFailed);
        }

        if (this.sessionPending || !this.session) {
          this.refs.summaryTotals.innerHTML = '<div class="zc-cod-summary__row"><span>Calculando precio…</span></div>';
          if (this.refs.submitPrice) this.refs.submitPrice.textContent = '';
          return;
        }

        var shippingCents = this.shippingCents();
        var extraDiscountCents = this.exitOfferCents();
        var promoCents = this.promoCents();
        var total = this.totalCents();
        var surcharge = this.matchingSurcharge();
        var shippingLabel = shippingCents === 0 ? 'Envío gratuito' : 'Envío' + (surcharge ? ' (' + surcharge.province + ')' : '');
        var shippingValue = shippingCents === 0 ? 'Gratis' : this.formatMoney(shippingCents);
        var hasDiscount = this.session.volumeDiscountBps > 0;

        var subtotalRow = hasDiscount
          ? '<div class="zc-cod-summary__row"><span>Subtotal</span><span class="zc-cod-summary__strike">' + this.formatMoney(this.session.catalogSubtotalCents) + '</span></div>' +
            '<div class="zc-cod-summary__row zc-cod-summary__row--discount"><span>Descuento pack</span><span>−' + this.formatMoney(this.session.catalogSubtotalCents - this.session.subtotalCents) + '</span></div>'
          : '<div class="zc-cod-summary__row"><span>Subtotal</span><span>' + this.formatMoney(this.session.subtotalCents) + '</span></div>';

        var extraDiscountRow = extraDiscountCents > 0
          ? '<div class="zc-cod-summary__row zc-cod-summary__row--discount"><span>Descuento extra (' + this.session.exitOfferPercent + '%)</span><span>−' + this.formatMoney(extraDiscountCents) + '</span></div>'
          : '';

        var promoRow = promoCents > 0
          ? '<div class="zc-cod-summary__row zc-cod-summary__row--discount"><span>Código ' + escapeHtml(this.promo.code) + '</span><span>−' + this.formatMoney(promoCents) + '</span></div>'
          : '';

        var upsellCents = this.upsellCents();
        var upsellRow = upsellCents > 0
          ? '<div class="zc-cod-summary__row"><span>' + escapeHtml(this.upsellShortLabel()) + '</span><span>' + this.formatMoney(upsellCents) + '</span></div>'
          : '';

        this.refs.summaryTotals.innerHTML =
          subtotalRow + extraDiscountRow + promoRow + upsellRow +
          '<div class="zc-cod-summary__row"><span>' + shippingLabel + '</span><span>' + shippingValue + '</span></div>' +
          '<div class="zc-cod-summary__row zc-cod-summary__row--total"><span>Total</span><span>' + this.formatMoney(total) + '</span></div>';

        if (this.refs.submitPrice) this.refs.submitPrice.textContent = this.formatMoney(total);
      }

      formatMoney(cents) {
        return EUR_FORMATTER.format(cents / 100);
      }

      /**
       * Standard Meta e-commerce parameters (content_ids/content_type/
       * contents), shared by every fbq() call this file makes. Without
       * these, Meta has no catalog item to attribute the event to — dynamic
       * ads and Advantage+ catalog campaigns silently stop optimizing
       * against real product data.
       */
      pixelContents() {
        var id = String(this.variantId);
        var ids = [id];
        var contents = [{ id: id, quantity: this.quantity }];
        if (this.upsellChecked && this.session && this.session.upsell) {
          var upsellId = String(this.session.upsell.variantId);
          ids.push(upsellId);
          contents.push({ id: upsellId, quantity: 1 });
        }
        // Same contents the server reports to Meta for this order (JCA Pay
        // lists the gift too); the value stays what is actually paid.
        if (this.session && this.session.gift) {
          var giftId = String(this.session.gift.variantId);
          ids.push(giftId);
          contents.push({ id: giftId, quantity: this.session.gift.quantity });
        }
        return { content_type: 'product', content_ids: ids, contents: contents };
      }

      generateEventId() {
        if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') return crypto.randomUUID();
        return 'zc-' + Date.now() + '-' + Math.random().toString(36).slice(2);
      }

      /**
       * Fires a client-side Meta Pixel event, tagged with this checkout
       * attempt's shared event_id so it can be deduplicated 1:1 against a
       * server-side Conversions API event carrying the same ID for the same
       * event name.
       */
      firePixel(eventName, params, eventId) {
        if (typeof window.fbq === 'function') {
          window.fbq('track', eventName, params, { eventID: eventId || this.eventId });
        }
      }
    }

    customElements.define('zc-cod-checkout', ZcCodCheckoutEl);
  }

  /** Wires every "Haz tu pedido" button to the global drawer — see blocks/zc-cod-button.liquid. */
  function initButtons() {
    document.querySelectorAll('[data-zc-cod-trigger]').forEach(function (btn) {
      if (btn.zcCodBound) return;
      btn.zcCodBound = true;
      btn.addEventListener('click', function () {
        var drawer = document.getElementById('zc-cod-drawer-checkout');
        if (drawer) drawer.openCheckout(btn);
      });
    });
  }

  /**
   * Design review only: on an UNPUBLISHED theme preview, opening the product
   * page with #zc-confirmacion-demo shows the post-order confirmation with
   * sample data, so it can be checked on a phone without placing a real
   * order. Never runs on the live theme (Shopify.theme.role === 'main').
   */
  function maybeShowConfirmationDemo() {
    if (window.location.hash !== '#zc-confirmacion-demo') return;
    var theme = window.Shopify && window.Shopify.theme;
    if (!theme || theme.role === 'main') return;
    var trigger = document.querySelector('[data-zc-cod-trigger]');
    var drawer = document.getElementById('zc-cod-drawer-checkout');
    if (!trigger || !drawer || typeof drawer.openCheckout !== 'function') return;

    drawer.openCheckout(trigger);
    drawer.isDemo = true;
    drawer.refs.fullName.value = 'Laura';
    var startedAt = Date.now();
    (function waitForPrice() {
      if (!drawer.session && Date.now() - startedAt < 4000) {
        setTimeout(waitForPrice, 150);
        return;
      }
      drawer.showConfirmation({
        order_name: '#DEMO',
        total_cents: drawer.session ? drawer.totalCents() : 3499,
        status_page_url: '#zc-confirmacion-demo',
      });
    })();
  }

  function init() {
    initButtons();
    window.zcCodReady = true;
    // A "Haz tu pedido" tap made before this script ran (see the
    // zc-cod-early-tap snippet): open the form for it now.
    var pending = window.zcCodPendingTrigger;
    window.zcCodPendingTrigger = null;
    var drawer = document.getElementById('zc-cod-drawer-checkout');
    if (pending && pending.isConnected && drawer && typeof drawer.openCheckout === 'function' && !(drawer.refs && drawer.refs.panel && drawer.refs.panel.open)) {
      drawer.openCheckout(pending);
    }
    maybeShowConfirmationDemo();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
  document.addEventListener('shopify:section:load', initButtons);
})();
